import { SERVER_RENDER_BREAKPOINT, inServer } from "core-web/constants";
import { breakpoints, labels } from "core-web/components/Breakpoints/breakpoints";

import { createContext } from "react";

const breakpointsArray = breakpoints;
const labelsArray = labels;

type BreakpointLabels = (typeof labelsArray)[number];
type Breakpoints = (typeof breakpointsArray)[number];

type BreakpointMapItem = {
    label: BreakpointLabels;
    size: Breakpoints;
};

type BreakpointMap = BreakpointMapItem[];

const breakpointMap = breakpointsArray.reduce((arr, size, index) => {
    return [
        ...arr,
        {
            label: labelsArray[index],
            size,
        },
    ];
}, [] as BreakpointMap);

/**
 * Get the value of a breakpoint with prefix
 * @param {string} label - Label of the breakpoint. Ex: 'md' or 'lg'
 * @returns {string} - '62em'
 */
const getMinBreakpointValue = (label: BreakpointLabels) => {
    if (!label) {
        return false;
    }
    const labelIndex = labels.indexOf(label);
    if (labelIndex > -1 && labelIndex < breakpoints.length) {
        return breakpoints[labelIndex];
    }
    console.error(`The breakpoint label ${label} does not exist`);
    return false;
};

const findBreakpoint = (label: BreakpointLabels, breakpointMap: BreakpointMap) =>
    breakpointMap.find((bp) => bp.label === label);

const getBreakpointValue = (label: BreakpointLabels | BreakpointMapItem) =>
    typeof label === "object" ? label.size : findBreakpoint(label, breakpointMap)?.size;

const getMaxBreakpointValue = (label: BreakpointLabels) => {
    if (label === null) {
        return;
    }
    /**
     * We do this to prevent collions between breakpoints.
     * https://www.w3.org/TR/mediaqueries-4/#range-context
     */
    const breakpointValue = getBreakpointValue(label)?.toString();
    const postfix = breakpointValue?.match(/[a-zA-Z]+/) || "";
    const value = parseInt(breakpointValue ?? "", 10);

    return `${value - (!!postfix.length && postfix[0] === "em" ? 0.01 : 1)}${postfix}`;
};

const getNextBreakpoint = (breakpoint: BreakpointLabels) => {
    const breakpointItem = findBreakpoint(breakpoint, breakpointMap);

    if (!breakpointItem) {
        return null;
    }

    const index = breakpointMap.indexOf(breakpointItem);
    return index !== breakpointMap.length - 1 ? breakpointMap[index + 1] : null;
};

type BreakpointObject = { [T in (typeof labelsArray)[number]]: string };

const above = breakpointMap.reduce((obj, bp) => {
    return {
        ...obj,
        [bp.label]: `@media (min-width: ${bp.size})`,
    };
}, {} as BreakpointObject);

const below = breakpointMap.reduce((obj, bp) => {
    return {
        ...obj,
        [bp.label]: `@media (max-width: ${getMaxBreakpointValue(bp.label)})`,
    };
}, {} as BreakpointObject);

const mediaItems = ["hover", "isIE"] as const;

type MediaObject = { [T in (typeof mediaItems)[number]]: string };

const media = mediaItems.reduce((obj) => {
    return {
        ...obj,
        hover: "@media (hover: hover), (-ms-high-contrast: none)",
        isIE: "@media all and (-ms-high-contrast: none)",
    };
}, {} as MediaObject);

type MediaBreakpointLabel = { name: string; from: string; to: string };
type MediaBetweenObject = {
    [key: string]: string;
};

const between = breakpointMap.reduce((obj, bp, breakpointMapIndex) => {
    /**
     * Create an array of min - max labels for each breakpoint
     * (xs-md, xs-lg etc)
     */
    const breakpointLabels = labelsArray
        .reduce<(MediaBreakpointLabel | null)[]>((arr, label, breakpointLabelIndex) => {
            return [
                ...arr,
                bp.label === label
                    ? null
                    : breakpointMapIndex < breakpointLabelIndex
                      ? {
                            name: `${bp.label}-${label}`,
                            from: bp.label,
                            to: label,
                        }
                      : null,
            ];
        }, [])
        .filter((bp) => bp !== null);

    /**
     * Create an array of CSS media queries from the breakpoint labels
     */
    const mediaQueries = breakpointLabels.reduce((obj, bpName) => {
        if (!bpName) {
            return obj;
        }

        const breakpointLabel = breakpointMap.find((bp) => bp.label === bpName.to)?.label;

        if (!breakpointLabel) {
            return obj;
        }

        return {
            ...obj,
            [bpName.name]: `@media (min-width: ${bp.size}) and (max-width: ${getMaxBreakpointValue(breakpointLabel)})`,
        };
    }, {});

    return {
        ...obj,
        ...mediaQueries,
    };
}, {} as MediaBetweenObject);

type SSRBreakpointItem = {
    breakpointLabel: BreakpointLabels;
    breakpointIndex: number;
};

export const Breakpoint = createContext<SSRBreakpointItem | null>(
    inServer
        ? null
        : window[SERVER_RENDER_BREAKPOINT as keyof typeof window] || {
              breakpointLabel: "mobileLg",
              breakpointIndex: labelsArray.indexOf("mobileLg"),
          },
);

// Used in SSR
const BreakpointCollector = Breakpoint.Provider;

export {
    BreakpointCollector,
    above,
    below,
    between,
    findBreakpoint,
    getBreakpointValue,
    getMaxBreakpointValue,
    getMinBreakpointValue,
    getNextBreakpoint,
    media,
};
